import type { BodyRow, EditedRow, EditorDataProps, TableData } from "@/src/types/table-data.types";
import { Button, Card, Collapse } from "react-bootstrap";
import { useCallback, useEffect, useMemo, useState } from "react";
import Input from "../Input/Input";
import Table from "./Table";
import { getMasterRow, getMasterRows, isNestedRow } from "@/src/utils/table-data";

const getTitle = (title: string) =>
  title
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const setDropdownData = (
  type?: string
): Array<[string, boolean | string | number]> | undefined => {
  if (type === "boolean") return [["True", true], ["False", false]];
  return undefined;
};

const cloneRow = (row: EditedRow): EditedRow => ({
  rowIndex: row.rowIndex,
  row: isNestedRow(row.row, true)
    ? [
        [...row.row[0]],
        ...row.row.slice(1).map((children) =>
          (children as BodyRow[]).map((childRow) =>
            Array.isArray(childRow[0])
              ? [[...(childRow[0] as string[])], ...childRow.slice(1)]
              : [...childRow],
          ),
        ),
      ] as unknown as BodyRow
    : [...row.row] as unknown as BodyRow,
});

export default function Editor({
  title="Transaction Editor",
  data,
  row = { rowIndex: -1, row: [] },
  config = [],
  onChanged,
  onSave,
  onDelete,
  onAdd,
  onRestore,
}: EditorDataProps) {
  
  const [open, setOpen] = useState(true);
  const [editedRow, setEditedRow] = useState<EditedRow>(() => cloneRow(row));
  const [editedDetailRows, setEditedDetailRows] = useState<EditedRow[]>([]);
  const [addedDetailRows, setAddedDetailRows] = useState<EditedRow[]>([]);
  const [isChanged, setIsChanged] = useState(false);
  const isNested = useMemo(() => Boolean(data.config.head.length>1), [data.config.head.length]);
  const masterValues = useMemo(
    () => isNestedRow(editedRow.row, true) ? editedRow.row[0] : editedRow.row as string[],
    [editedRow.row]
  );
  
  useEffect(() => {
    onChanged?.(isChanged);
  }, [isChanged, onChanged]);

  const handleChanged = useCallback((value: string, idx: number) => {
    setEditedRow((prev) => {
      const nested = isNestedRow(prev.row, true);
      const values = nested ? [...prev.row[0]] : [...(prev.row as string[])];
      if (idx >= 0) values[idx] = value;
      else values.push(value);
      if (nested) {
        const detailRows = (prev.row as [string[], string[][]])[1];
        return { rowIndex: prev.rowIndex, row: [values, detailRows] };
      }
      return { rowIndex: prev.rowIndex, row: values };
    });
    setIsChanged(true);
  }, []);

  const handleTableChanged = useCallback((updates: EditedRow[]) => {
    setEditedDetailRows((prev) => {
    const next = [...prev];
    updates.forEach((update) => {
      const idx = next.findIndex((item) => item.rowIndex === update.rowIndex);
      if (idx >= 0) next[idx] = update;
      else next.push(update);
    });
    return next;
  });

    setIsChanged(true);
  }, []);

  const handleSave = async () => {
    if (!isChanged) return;
    const text = `Are you sure to ${config[0]?.title || "Save Entry"}?`;
    if (!confirm("Are you sure to "+text+"?")) return

    const saved = await onSave({
      master: getMasterRow(editedRow.row),
      detail: getMasterRows(editedDetailRows)
    });
    if (saved === false) {
      handleRestore();
      return;
    }
    setIsChanged(false);
  };

  const handleRestore = () => {
    setEditedRow(cloneRow(row));
    setEditedDetailRows([]);
    setAddedDetailRows([]);
    setIsChanged(false);
    onRestore?.(row.rowIndex);
  };

  const handleAdd = () => {
    const master = data.config.head[0][0].map(([column]) =>
      data.config.primaryKey[0][0].includes(column) ? null : ""
    );
    const nextRow: EditedRow = {
      rowIndex: -1,
      row: (isNested ? [master, []] : master) as EditedRow["row"],
    };

    setEditedRow(nextRow);
    setEditedDetailRows([]);
    setAddedDetailRows([]);
    setIsChanged(true);
    onAdd?.();
  };

  const handleDetailAdd = () => {
    if (!isNestedRow(editedRow.row, true)) return;

    const detail = (data.config.head[1][0] ?? []).map(([column]) =>
      data.config.primaryKey[1][0]?.includes(column) ? null : ""
    );
    const rowIndex = editedRow.row[1].length + addedDetailRows.length;
    const nextRow = { rowIndex, row: detail as string[] };

    setAddedDetailRows((prev) => [...prev, nextRow]);
    setEditedDetailRows((prev) => {
      return [...prev, nextRow];
    });
    setIsChanged(true);
  };

  const detailBody = useMemo(() => {
    if (!isNested || !isNestedRow(editedRow.row, true)) return [];
    const existingRows = editedRow.row[1].map(
      (detailRow, rowIndex) =>
        (
          editedDetailRows.find(
            (update) => update.rowIndex === rowIndex
          )?.row as string[] | undefined
        ) ?? detailRow
    );
    const appendedRows = addedDetailRows.map(({ row, rowIndex }) =>
      (editedDetailRows.find((update) => update.rowIndex === rowIndex)?.row as string[] | undefined) ?? row as string[]
    );

    return [...existingRows, ...appendedRows];
  }, [addedDetailRows, editedDetailRows, editedRow.row, isNested]);

  const handleDelete = () => {
    const text = `Are you sure to ${config[1]?.title || "Save Entry"}?`;
    if (!confirm("Are you sure to "+text+"?")) return
    onDelete(editedRow);
  };

  const detail = (data: TableData) => {
    if (!isNested) return null;

    return (
      <div className="d-flex flex-wrap gap-1 px-4">
        <div className="border-bottom border-2 mt-2 w-100">
          <small>
            <span>Details</span>
          </small>
        </div>
        <Card>
          <Card.Body className="p-1">
            <div className="d-flex justify-content-end mb-1">
              <button type="button" className="btn btn-sm btn-outline-secondary px-1 py-0 d-inline-flex align-items-center gap-1" style={{ fontSize: "0.75rem" }} onClick={handleDetailAdd}>
                <i className="bi bi-plus-lg" aria-hidden="true" />
                Add
              </button>
            </div>
            <Table data={data} tableAt={ {level: 1, index: 0} } handleRowValuesChanged={handleTableChanged} />
          </Card.Body>
        </Card>
      </div>
    );
  };

  return (
    <Card className="mb-3">
      <Card.Header
        className="py-1 px-2 w-100 d-flex justify-content-between align-items-center"
        title={open ? "Click to close editor" : "Click to open editor"}
        style={{ cursor: "pointer" }}
        onClick={() => setOpen(!open)}
      >
        <span>Editor</span>
        <i className={`bi bi-chevron-${open ? "down" : "right"}`}></i>
      </Card.Header>

      <Collapse in={open}>
        <Card.Body className="p-0 pb-4">
          <div className="d-flex align-items-center justify-content-between px-4 py-5 bg-blue-50 border-bottom border-secondary-subtle">
            <div className="d-flex align-items-center gap-4">
              <div className="d-flex align-items-center justify-content-center w-10 h-10 text-white bg-primary rounded-3">
                <i className="bi bi-journal-text fs-4"></i>
              </div>
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="m-0 fw-bold text-dark">{title}</h5>
                  {isChanged && (
                    <span className="d-inline-flex align-items-center px-2 py-1 rounded-pill small fw-medium bg-warning-subtle text-warning-emphasis border border-warning-subtle shadow-sm">
                      <span
                        className="d-inline-block rounded-circle bg-warning me-1"
                        style={{ width: "0.375rem", height: "0.375rem" }}
                        aria-hidden="true"
                      />
                      Editing Mode
                    </span>
                  )}
                </div>
                <p className="m-0 fs-xs text-secondary">Create or modify { title } entries</p>
              </div>
            </div>
            <div className="d-flex flex-column align-items-end gap-2">
              <div className="d-flex align-items-center gap-4">
                <Button
                  className="border-0 btn-naked fs-6 fw-normal"
                  onClick={handleRestore}
                  disabled={!isChanged}
                >
                  Restore All
                </Button>
                <Button
                  className="py-2 px-4 fs-6 fw-bolder"
                  onClick={handleSave}
                  disabled={!isChanged}
                >
                  {config[0]?.title || "Save Entry"}
                </Button>
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-sm btn-outline-secondary px-2 py-0" onClick={handleDelete} disabled={isChanged}>
                  {config[1]?.title || "Reversal"}
                </button>
                <button type="button" className="btn btn-sm btn-secondary px-2 py-0 d-inline-flex align-items-center gap-1 shadow-sm" onClick={handleAdd} disabled={isChanged}>
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                  {config[2]?.title || "Add"}
                </button>
              </div>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-1 px-4 pt-4">
            {
              data.config.head[0][0].map((key, idx) => {
                if (key[1].hidden) return null;
                return (
                  <div key={`${key[0]}`} className="w-auto">
                    <Input
                      className="form-control form-control-sm w-auto"
                      labelClassName="fs-075 mb-1"
                      label={getTitle(key[0])}
                      value={masterValues[idx] ?? ""}
                      type={key[1].type as string}
                      onChanged={(value) => handleChanged(value, idx)}
                      readOnly={key[1].readOnly ? key[1].readOnly as boolean : false}
                      options={setDropdownData(key[1].type as string)}
                    />
                  </div>
                )
              })
            }
          </div>
          {
            detail({
              body: detailBody,
              config: {...data.config}              
            })
          }
        </Card.Body>
      </Collapse>
    </Card>
  );
};
